/**
 * App Configuration API — managed app configuration delivered via MDM policies.
 *
 * Smplify backend has no dedicated app-config endpoint; instead app configs
 * are stored as MDM policies whose name follows the convention:
 *
 *   "AppConfig — {bundleId} — {configName}"
 *
 * This is identical to the PPPC linking pattern. The single payload uses the
 * Apple `InstallApplication` command with `InstallAsManaged: true` and the
 * `Configuration` field set to the plain key/value dictionary the admin
 * entered (NEVER stringified — Apple expects a real plist dict).
 *
 * The Smplify backend handles re-pushing via Settings/ApplicationConfiguration
 * for already-installed apps automatically once the policy exists.
 */
import {
  createPolicy,
  deletePolicy,
  listPolicies,
  updatePolicy,
} from "./policiesApi";
import { CreatePolicyRequest, PolicyDetails, PolicyPayload } from "@/types/api";

export const APP_CONFIG_PREFIX = "AppConfig";
export const APP_CONFIG_SEPARATOR = " — "; // em dash, matches PPPC convention
export const APP_CONFIG_INSTALL_IDENTIFIER = "InstallApplication";

export type AppConfigKeyType = "string" | "integer" | "boolean" | "array";

export interface AppConfigKey {
  /** A unique client-side row id (used for stable React keys). */
  rowId: string;
  key: string;
  type: AppConfigKeyType;
  /** Editor value — always stored as a string in the form, coerced on save. */
  value: string;
}

export interface AppConfigSnapshot {
  policyId: string;
  policyName: string;
  configName: string;
  bundleId: string;
  keys: AppConfigKey[];
  /** Raw configuration dict as stored on the policy (post-coercion). */
  configuration: Record<string, unknown>;
  updatedAt?: string;
}

export interface SaveAppConfigArgs {
  existingPolicyId?: string;
  appName: string;
  bundleId: string;
  configName: string;
  keys: AppConfigKey[];
  /** Optional tenant scope for the underlying policy. Empty/undefined falls
   *  back to no scope (existing behaviour). */
  tenantIds?: string[];
  allTenants?: boolean;
}

// ---------------------------------------------------------------------------
// Naming convention helpers
// ---------------------------------------------------------------------------

export function buildAppConfigName(bundleId: string, configName: string): string {
  return `${APP_CONFIG_PREFIX}${APP_CONFIG_SEPARATOR}${bundleId}${APP_CONFIG_SEPARATOR}${configName}`;
}

export function buildAppConfigPrefixForApp(bundleId: string): string {
  return `${APP_CONFIG_PREFIX}${APP_CONFIG_SEPARATOR}${bundleId}`;
}

/**
 * Parse "AppConfig — {bundleId} — {configName}" → { configName }.
 * Returns null if the policy name doesn't follow the convention for this app.
 */
export function parseAppConfigName(
  policyName: string | undefined,
  bundleId: string,
): { configName: string } | null {
  if (!policyName || !bundleId) return null;
  const prefix = buildAppConfigPrefixForApp(bundleId) + APP_CONFIG_SEPARATOR;
  if (!policyName.startsWith(prefix)) return null;
  const configName = policyName.slice(prefix.length).trim();
  if (!configName) return null;
  return { configName };
}

export function isAppConfigForApp(policy: PolicyDetails, bundleId: string): boolean {
  return parseAppConfigName(policy.name, bundleId) !== null;
}

/**
 * Parse "AppConfig — {bundleId} — {configName}" without knowing the bundleId
 * in advance. Used by the cross-app aggregate view.
 */
export function parseAnyAppConfigName(
  policyName: string | undefined,
): { bundleId: string; configName: string } | null {
  if (!policyName) return null;
  const prefix = APP_CONFIG_PREFIX + APP_CONFIG_SEPARATOR;
  if (!policyName.startsWith(prefix)) return null;
  const rest = policyName.slice(prefix.length);
  const sepIdx = rest.indexOf(APP_CONFIG_SEPARATOR);
  if (sepIdx <= 0) return null;
  const bundleId = rest.slice(0, sepIdx).trim();
  const configName = rest.slice(sepIdx + APP_CONFIG_SEPARATOR.length).trim();
  if (!bundleId || !configName) return null;
  return { bundleId, configName };
}

/**
 * List ALL managed app configurations across every bundleId in a single
 * policies fetch. Used by the standalone /configurations/app-config page.
 */
export async function listAllAppConfigs(): Promise<AppConfigSnapshot[]> {
  const policies = await listPolicies();
  const out: AppConfigSnapshot[] = [];
  for (const policy of policies) {
    const parsed = parseAnyAppConfigName(policy.name);
    if (!parsed) continue;
    const snap = snapshotFromPolicy(policy, parsed.bundleId);
    if (snap) out.push(snap);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Type coercion (form → JSON dict)
// ---------------------------------------------------------------------------

let rowIdCounter = 0;
export function makeRowId(): string {
  rowIdCounter += 1;
  return `kv-${Date.now().toString(36)}-${rowIdCounter}`;
}

export function emptyKeyRow(): AppConfigKey {
  return { rowId: makeRowId(), key: "", type: "string", value: "" };
}

/**
 * Coerce one editor row to its real JSON value. Throws on invalid integer
 * input so the dialog can surface a precise error.
 */
export function coerceValue(row: AppConfigKey): unknown {
  switch (row.type) {
    case "boolean":
      return row.value === "true";
    case "integer": {
      const n = Number.parseInt(row.value, 10);
      if (!Number.isFinite(n)) {
        throw new Error(`"${row.key}" must be a whole number.`);
      }
      return n;
    }
    case "array":
      return row.value
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    case "string":
    default:
      return row.value;
  }
}

export function coerceKeysToDict(keys: AppConfigKey[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const row of keys) {
    if (!row.key) continue;
    out[row.key] = coerceValue(row);
  }
  return out;
}

/**
 * Inverse of `coerceKeysToDict` — used in edit mode to rebuild the editor
 * rows from a stored Configuration dict, inferring the original type per
 * value.
 */
export function keysFromDict(dict: Record<string, unknown> | undefined): AppConfigKey[] {
  if (!dict || typeof dict !== "object") return [];
  return Object.entries(dict).map(([key, raw]) => {
    let type: AppConfigKeyType = "string";
    let value = "";
    if (typeof raw === "boolean") {
      type = "boolean";
      value = raw ? "true" : "false";
    } else if (Array.isArray(raw)) {
      type = "array";
      value = raw.map((v) => String(v)).join(", ");
    } else if (typeof raw === "number" && Number.isInteger(raw)) {
      type = "integer";
      value = String(raw);
    } else {
      type = "string";
      value = raw == null ? "" : String(raw);
    }
    return { rowId: makeRowId(), key, type, value };
  });
}

// ---------------------------------------------------------------------------
// Snapshot parsing
// ---------------------------------------------------------------------------

function parseInstallPayload(
  payload: PolicyPayload | undefined,
): { bundleId: string; configuration: Record<string, unknown> } {
  if (!payload?.configJson) return { bundleId: "", configuration: {} };
  try {
    const cfg = JSON.parse(payload.configJson) as {
      Identifier?: string;
      Configuration?: Record<string, unknown>;
    };
    return {
      bundleId: cfg.Identifier ?? "",
      configuration:
        cfg.Configuration && typeof cfg.Configuration === "object"
          ? cfg.Configuration
          : {},
    };
  } catch {
    return { bundleId: "", configuration: {} };
  }
}

function snapshotFromPolicy(
  policy: PolicyDetails,
  bundleId: string,
): AppConfigSnapshot | null {
  const parsed = parseAppConfigName(policy.name, bundleId);
  if (!parsed) return null;
  const installPayload = (policy.payloads ?? []).find(
    (p) => p.identifier === APP_CONFIG_INSTALL_IDENTIFIER,
  );
  const { bundleId: payloadBundleId, configuration } =
    parseInstallPayload(installPayload);
  return {
    policyId: policy.id,
    policyName: policy.name ?? "",
    configName: parsed.configName,
    bundleId: payloadBundleId || bundleId,
    keys: keysFromDict(configuration),
    configuration,
    updatedAt: policy.updatedAt,
  };
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export async function listAppConfigsForApp(
  bundleId: string,
): Promise<AppConfigSnapshot[]> {
  if (!bundleId) return [];
  const policies = await listPolicies();
  return policies
    .map((p) => snapshotFromPolicy(p, bundleId))
    .filter((s): s is AppConfigSnapshot => s !== null);
}

export async function saveAppConfig(
  args: SaveAppConfigArgs,
): Promise<{ id: string }> {
  const {
    existingPolicyId,
    appName,
    bundleId,
    configName,
    keys,
    tenantIds,
    allTenants,
  } = args;
  const configuration = coerceKeysToDict(keys);

  const request: CreatePolicyRequest = {
    name: buildAppConfigName(bundleId, configName),
    description: `Managed App Configuration for ${appName}`,
    payloads: [
      {
        kind: "command",
        identifier: APP_CONFIG_INSTALL_IDENTIFIER,
        config: {
          Identifier: bundleId,
          InstallAsManaged: true,
          // IMPORTANT: plain object — never JSON.stringify this. Apple expects
          // a real plist dictionary in the MDM payload.
          Configuration: configuration,
        },
      },
    ],
    ...(allTenants
      ? { allTenants: true }
      : tenantIds && tenantIds.length > 0
        ? { tenantIds }
        : {}),
  };

  if (existingPolicyId) {
    await updatePolicy(existingPolicyId, request);
    return { id: existingPolicyId };
  }
  return createPolicy(request);
}

export async function deleteAppConfig(id: string): Promise<string> {
  return deletePolicy(id);
}
